/* Shabbat closure — the workshop does not trade from Friday evening to
   Saturday night, Jerusalem time, and that applies to every visitor wherever
   they are.

   Everything below compares UTC instants. Sunset is computed as a UTC time,
   `Date.now()` is a UTC instant, so no timezone conversion is involved in
   deciding whether the store is open — only in printing the times, which is
   what Intl is for. A visitor in Sydney and one in Los Angeles are closed at
   the same real moment.

   Two offsets, both adjustable in the theme editor because they are a
   halachic choice and not a technical one:
     candle   minutes BEFORE Friday sunset that Shabbat begins.
              Jerusalem's custom is 40; most cities use 18.
     havdalah minutes AFTER Saturday sunset that it ends (nightfall).
              42 is the common reckoning; Rabbeinu Tam is 72.
*/
(function (root) {
  'use strict';

  var LAT = 31.7683, LON = 35.2137;          // Jerusalem
  var TZ = 'Asia/Jerusalem';
  var rad = Math.PI / 180;

  /* NOAA sunset, returned as a UTC Date.
     `date` is used only for its calendar day — the result is the sunset of
     that day at the given latitude and longitude. */
  function sunsetUTC(date, lat, lon) {
    var jdMidnight = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(),
                              date.getUTCDate()) / 86400000 + 2440587.5;
    var n = Math.ceil(jdMidnight - 2451545.0 + 0.0008);
    /* MINUS longitude/360, with longitude east-positive. East of Greenwich
       the sun crosses the meridian EARLIER than it does at UTC noon, so the
       term has to pull the transit back. Adding it instead put Jerusalem's
       solar noon at 14:26Z rather than 09:39Z — a sunset just after midnight. */
    var Jstar = n - lon / 360;                          // mean solar noon
    var M = (357.5291 + 0.98560028 * Jstar) % 360;      // solar mean anomaly
    var C = 1.9148 * Math.sin(M * rad)
          + 0.0200 * Math.sin(2 * M * rad)
          + 0.0003 * Math.sin(3 * M * rad);             // equation of centre
    var lam = (M + C + 180 + 102.9372) % 360;           // ecliptic longitude
    var Jtransit = 2451545.0 + Jstar + 0.0053 * Math.sin(M * rad)
                 - 0.0069 * Math.sin(2 * lam * rad);
    var decl = Math.asin(Math.sin(lam * rad) * Math.sin(23.44 * rad));
    /* -0.833° accounts for refraction and the sun's radius: the moment the
       upper limb touches the horizon, which is what "sunset" means. */
    var cosOmega = (Math.sin(-0.833 * rad) - Math.sin(lat * rad) * Math.sin(decl))
                 / (Math.cos(lat * rad) * Math.cos(decl));
    if (cosOmega > 1 || cosOmega < -1) return null;     // no sunset that day
    var omega = Math.acos(cosOmega) / rad;
    var Jset = Jtransit + omega / 360;
    return new Date((Jset - 2440587.5) * 86400000);
  }

  /* Which calendar day is it in Jerusalem right now? Asking in Jerusalem's
     own timezone matters: at 23:00 UTC on a Friday it is already Saturday
     there, and the wrong day gives the wrong sunset. */
  function jerusalemParts(at) {
    var f = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(at).reduce(function (o, p) { o[p.type] = p.value; return o; }, {});
    return {
      weekday: f.weekday,
      utcMidday: new Date(Date.UTC(+f.year, +f.month - 1, +f.day, 12))
    };
  }

  /* The Shabbat window containing or following `at`.

     Measured against the almanac this sunset is right to within about two
     minutes — three of four spot checks landed on the minute, December came
     out two minutes early. Two minutes matters here, so both defaults are
     deliberately generous and err in the safe direction:

       candle 40   Jerusalem's own custom, and forty minutes before sunset is
                   far enough ahead that a two-minute error cannot make the
                   store close late.
       havdalah 45 nightfall is commonly reckoned at sunset + 42; the extra
                   three cover the same error so the store cannot reopen
                   early. Reopening late costs a few minutes of trade.
                   Reopening early is the failure that actually matters.

     Both are settings. Anyone relying on this should check the times against
     an authoritative calendar and adjust — this is astronomy, not psak. */
  function windowFor(at, candle, havdalah) {
    candle = candle == null ? 40 : candle;
    havdalah = havdalah == null ? 45 : havdalah;

    /* Start a day behind: on a Saturday afternoon the Friday that opened the
       current window is already in the past, and that is the window we want.
       Then walk forward far enough to clear a whole week. */
    for (var off = -1; off <= 8; off++) {
      var probe = new Date(at.getTime() + off * 86400000);
      var p = jerusalemParts(probe);
      if (p.weekday !== 'Fri') continue;

      var friSunset = sunsetUTC(p.utcMidday, LAT, LON);
      if (!friSunset) continue;
      var start = new Date(friSunset.getTime() - candle * 60000);

      var satNoon = new Date(p.utcMidday.getTime() + 86400000);
      var satSunset = sunsetUTC(satNoon, LAT, LON);
      var end = new Date(satSunset.getTime() + havdalah * 60000);

      if (at < end) return { start: start, end: end, closed: at >= start && at < end };
    }
    return null;
  }

  /* Only the timezone is fixed. Asking for a date used to inherit the
     default hour and minute as well, so every cell read "Fri 07 Aug, 18:51,
     18:51" — the caller says what it wants. */
  function fmt(d, opts) {
    return new Intl.DateTimeFormat('en-GB', Object.assign(
      { timeZone: TZ, hour12: false }, opts || {}
    )).format(d);
  }
  function time(d) { return fmt(d, { hour: '2-digit', minute: '2-digit' }); }

  root.Shabbat = {
    sunsetUTC: sunsetUTC,
    windowFor: windowFor,
    format: fmt,
    time: time,
    /* the next N windows, for the times page */
    upcoming: function (from, n, candle, havdalah) {
      var out = [], at = from || new Date();
      for (var i = 0; i < (n || 8); i++) {
        var w = windowFor(at, candle, havdalah);
        if (!w) break;
        out.push(w);
        at = new Date(w.end.getTime() + 3600000);
      }
      return out;
    }
  };
})(typeof module !== 'undefined' && module.exports ? module.exports : window);
